'use client';

import { notFound } from 'next/navigation';

import { useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

import { Card, Spinner } from '@uikit';

import {
  usePublicUserQuery,
  useUserFollowedAppsInfiniteQuery,
  useUserVotedAppsInfiniteQuery,
} from '@query';

import PageHeader from '@store:web/components/layout/PageHeader/PageHeader';

import AppBlock from '../UserPage/AppBlock';

const getFetchFunc = (type: 'follows' | 'validations') => {
  if (type === 'follows') {
    return useUserFollowedAppsInfiniteQuery;
  }

  return useUserVotedAppsInfiniteQuery;
};

const UserApps = ({
  userId,
  pageType,
}: {
  userId: string;
  pageType: 'follows' | 'validations';
}) => {
  const queryFunc = getFetchFunc(pageType);
  const {
    data: appsPages,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
  } = queryFunc({ limit: 20, id: userId });
  const { data: user } = usePublicUserQuery({ searchParams: { id: userId } });
  const { ref } = useInView({
    onChange: (inView) => inView && !isLoading && !isFetchingNextPage && fetchNextPage(),
  });
  const apps = useMemo(() => appsPages?.pages.flatMap((page) => page) || [], [appsPages]);

  if (!user) {
    return notFound();
  }

  return (
    <>
      <PageHeader
        page="user"
        disableBanner
        extendBreadcrumbs={[
          {
            label: `@${user?.attributes.magicId}`,
            pathname: `/user/${user?.attributes.magicId}`,
          },
          {
            label: pageType === 'follows' ? 'Follows' : 'Participants in validation',
            pathname: `/user/${user?.attributes.magicId}/${pageType}`,
          },
        ]}
      />
      <main className="v2-container">
        <Card
          className="public-profile-card md:overflow-hidden rounded-none md:rounded md:px-6 md:py-8 max-md:shadow-none"
          isDefaultPadding={false}
        >
          <h1 className="t-h3 mb-5 md:mb-10">
            {pageType === 'follows' ? 'Follows' : 'Participants in validation'}
          </h1>
          <AppBlock isHeader={false} apps={apps} isFull={false} blockClassName="gap-8" />
          {(isLoading || hasNextPage) && (
            <Spinner
              key={appsPages?.pages.length}
              ref={ref}
              className="self-center justify-self-center m-auto"
              size="md"
            />
          )}
        </Card>
      </main>
    </>
  );
};

export default UserApps;
